import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { heiProgramsFilters } from "../filterConfigurations";
import SimpleToolbar from "../../common/SimpleToolbar";

const HeiPrograms = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["HEI Programs"]?.edit;
  const {
    data: heiPrograms,
    loading,
    error,
  } = useFirestore("HeiPrograms", "HEIProgramCode", "asc");
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "HEI Programs";

  // Use custom filter hook
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(heiPrograms || [], heiProgramsFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getBooleanDisplayValue = (value) => {
    if (value === 0 || value === "0") return "No";
    if (value === -1 || value === "-1") return "Yes";
    if (value === null || typeof value === "undefined") return "";
    return String(value);
  };

  const columns = useMemo(
    () => [
      {
        field: "HEIProgramCode",
        headerName: "HEI Program Code",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "HEIProgramName",
        headerName: "HEI Program Name",
        flex: 2,
        minWidth: 200,
      },
      {
        field: "Active",
        headerName: "Active",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((program, index) => ({
      id: program.id || index, // Use program.id if available, otherwise use index
      ...program,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedProgram(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedProgram(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching programs:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={heiProgramsFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={{}}
          title="HEI Programs Filters"
          resultCount={filteredData.length}
          totalCount={heiPrograms?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="HeiPrograms"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={heiPrograms}
              templateFields={columns.map((col) => col.field)}
            />
          )}
          {canEdit && <ClearCollectionButton collectionName="HeiPrograms" />}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: SimpleToolbar,
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
        {/* Pagination Controls */}
        {/* (Removed bottom pagination controls) */}
      </div>
      {selectedProgram && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          programDetail={selectedProgram}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HeiPrograms;
